import React from 'react';

export type SpinnerSize = 'sm' | 'md' | 'lg';

// Ring dimensions mirror the analytics `LoadingState` primitive so the loading
// animation is visually consistent across the whole app.
const SIZE_CLASSES: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4 border-2',
  md: 'h-8 w-8 border-4',
  lg: 'h-10 w-10 border-4',
};

interface SpinnerProps {
  size?: SpinnerSize;
  className?: string;
}

/**
 * Bare ring spinner matching the analytics pages' formatting: a light-pink ring
 * with a darker pink leading segment. Use inside custom layouts or overlays.
 */
export const Spinner: React.FC<SpinnerProps> = ({ size = 'lg', className = '' }) => (
  <div
    role="status"
    aria-label="Loading"
    className={`${SIZE_CLASSES[size]} rounded-full border-pink-200 border-t-pink-500 animate-spin ${className}`.trim()}
  />
);

interface LoadingStateProps {
  label?: string;
  size?: SpinnerSize;
  /** Wrapper spacing/height classes; defaults to the analytics `py-20` block. */
  className?: string;
}

/**
 * Centered spinner + label block, matching the analytics `LoadingState`
 * primitive. Shared so every page renders the same loading treatment.
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  label = 'Loading…',
  size = 'lg',
  className = 'py-20',
}) => (
  <div
    className={`flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 ${className}`.trim()}
  >
    <Spinner size={size} />
    {label && <p className="mt-4 text-sm">{label}</p>}
  </div>
);

export default Spinner;
